import { ElementRef } from '@angular/core';

/**
 * Utility class for password visibility toggle functionality
 * Provides a reusable method to toggle password field visibility
 */
export class PasswordToggleUtil {
  /**
   * Toggles the visibility of a password input field
   * @param inputRef - ElementRef of the password input element
   * @param isVisible - Current visibility state
   * @returns New visibility state (toggled)
   */
  static toggleVisibility(inputRef: ElementRef | undefined, isVisible: boolean): boolean {
    if (!inputRef) {
      return isVisible;
    }

    const input = inputRef.nativeElement;
    const currentPosition = input.selectionStart;
    const newVisibility = !isVisible;
    
    input.type = newVisibility ? 'text' : 'password';
    
    // Restore cursor position after type change
    setTimeout(() => {
      if (input.setSelectionRange) {
        input.setSelectionRange(currentPosition, currentPosition);
      }
    }, 0);

    return newVisibility;
  }

  /**
   * Generic toggle method that accepts an input map and field name
   * Useful for components with multiple password fields
   * @param inputMap - Map of field names to ElementRefs
   * @param visibilityMap - Map of field names to visibility states
   * @param field - The field name to toggle
   */
  static toggleField<T extends string>(
    inputMap: Record<T, ElementRef | undefined>,
    visibilityMap: Record<T, boolean>,
    field: T
  ): void {
    const inputRef = inputMap[field];
    if (inputRef) {
      visibilityMap[field] = PasswordToggleUtil.toggleVisibility(inputRef, visibilityMap[field]);
    }
  }
}
